import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useCatalogueContext } from "../../contexts/CatalogueContext/CatalogueContext";
import { addDatadogAction } from "../../services/datadog";
import "./ErrorBoundaryPage.scss";

// Per-session set of error fingerprints already reported. Module-level so
// it survives unmount/remount cycles in the same SPA session.
const reportedErrors = new Set<string>();

const ErrorBoundaryPage: React.FC = () => {
	const { state: errorMessage } = useCatalogueContext((state) => state.errorMessage);
	const navigate = useNavigate();
	const routerLocation = useLocation();
	const firedRef = useRef(false);
	const { t } = useTranslation();

	useEffect(() => {
		if (firedRef.current) return;
		if (!errorMessage) return;

		// Location / stall context may be unavailable when an error boundary
		// triggers, so all context fields are optional.
		const route = routerLocation?.pathname;
		const fingerprint = `${errorMessage}|${route ?? ""}`;

		if (reportedErrors.has(fingerprint)) {
			firedRef.current = true;

			return;
		}

		reportedErrors.add(fingerprint);
		firedRef.current = true;

		addDatadogAction("error_boundary_caught", {
			errorName: undefined,
			errorMessage,
			errorStack: undefined,
			route,
		});
	}, [errorMessage, routerLocation]);

	const handleGoHome = () => {
		navigate("/");
	};

	return (
		<div className="ErrorBoundaryPage">
			<h1 className="ErrorTitle">{t("errorBoundary.title")}</h1>
			<div className="ErrorMessage">{errorMessage}</div>
			<button className="GoHomeButton" onClick={handleGoHome}>
				{t("errorBoundary.goHome")}
			</button>
		</div>
	);
};

// Exported for testing — clears the fired set between scenarios.
export const __resetReportedErrorsForTesting = (): void => {
	reportedErrors.clear();
};

export default ErrorBoundaryPage;
